/**
 * Referral attribution (VW-G1). Web half of viral-backend V-G: the invite code
 * must survive the hop from an invite link to signup/install so the backend can
 * grant credits on activation.
 *
 * Storage is localStorage because attribution has to survive the round trip
 * through login.manticscore.com or an App Store install, both of which lose
 * sessionStorage.
 */

const REFERRAL_STORAGE_KEY = "manticscore.referral.v1";
/** First-touch wins for this long; after that a newer invite may overwrite. */
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type Referral = {
  code: string;
  /** Where the code came from: an /invite/:code path or a ?ref= param. */
  source: "invite_path" | "ref_param";
  capturedAt: number;
};

/**
 * What `GET /v1/invites/{code}` actually returns — and all it ever will.
 *
 * This type used to promise `display_name`, `avatar_url`, `clip_slug` and
 * `clip_title`, and the landing page rendered all four. The endpoint returns
 * `{code, active}` and nothing else, deliberately: a referral link is shared
 * publicly, so anything identifying about the referrer turns the lookup into
 * an enumeration oracle over user identities. The route's docstring says so.
 *
 * So the extra fields were never populated, every branch that depended on them
 * was dead, and the page silently rendered its own fallback copy. Narrowing
 * the type is what makes that visible instead of merely inert.
 */
export type InviteLookup = {
  code: string;
  /** Whether the code can still be activated. Revoked codes still resolve. */
  active: boolean;
};

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.manticscore.com";

/**
 * Invite codes are user-visible strings. Normalising rather than rejecting
 * keeps a code that was typed by hand or lower-cased by a mail client working.
 */
export function normalizeInviteCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
  if (cleaned.length < 4 || cleaned.length > 32) return null;
  return cleaned;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Attribution is best-effort; never break the landing on a storage error.
  }
}

export function loadReferral(): Referral | null {
  const stored = safeGet(REFERRAL_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as Referral;
    if (!parsed?.code) return null;
    if (Date.now() - parsed.capturedAt > REFERRAL_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Records a referral. First touch wins within the TTL — the person who
 * actually made the introduction should get the credit, not whoever's link
 * happened to be clicked last before signup.
 */
export function recordReferral(code: string, source: Referral["source"]): Referral | null {
  const normalized = normalizeInviteCode(code);
  if (!normalized) return null;

  const existing = loadReferral();
  if (existing) return existing;

  const referral: Referral = { code: normalized, source, capturedAt: Date.now() };
  safeSet(REFERRAL_STORAGE_KEY, JSON.stringify(referral));
  return referral;
}

export function clearReferral(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Nothing useful to do.
  }
}

/** Reads a `?ref=` code off any URL. Used by the landing page, not just /invite. */
export function referralFromSearch(search: string): string | null {
  try {
    return normalizeInviteCode(new URLSearchParams(search).get("ref"));
  } catch {
    return null;
  }
}

/**
 * Appends the stored code to a signup/install URL. The backend
 * (viral-backend V-G) reads `ref` on activation to issue the grant.
 */
export function withReferralCode(url: string, code?: string | null): string {
  const referralCode = normalizeInviteCode(code ?? null) ?? loadReferral()?.code;
  if (!referralCode) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}ref=${encodeURIComponent(referralCode)}`;
}

/**
 * Checks that a code is real. Unauthenticated and best-effort: a code the
 * backend does not know still lands on a usable page.
 *
 * It cannot name the inviter, and no version of this function will be able to
 * — see `InviteLookup`. The landing page is written for a nameless inviter as
 * a result, which is the honest shape rather than a degraded one.
 */
export async function lookupInvite(
  code: string,
  signal?: AbortSignal,
): Promise<InviteLookup | null> {
  const normalized = normalizeInviteCode(code);
  if (!normalized) return null;

  try {
    const response = await fetch(
      `${API_BASE_URL}/v1/invites/${encodeURIComponent(normalized)}`,
      { signal, headers: { Accept: "application/json" } },
    );
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<InviteLookup>;
    if (typeof body?.code !== "string") return null;
    return { code: body.code, active: body.active !== false };
  } catch {
    return null;
  }
}
